import { useState } from 'react';

export type TransactionType = 'income' | 'expense' | 'transfer';

interface TransactionTypeSelectorProps {
  onTypeSelect: (type: TransactionType) => void;
  selectedType?: TransactionType;
}

const transactionTypes = [
  {
    id: 'income' as TransactionType,
    title: 'Приход',
    subtitle: 'Поступления на счет',
    icon: '💰',
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50',
    textColor: 'text-emerald-700'
  },
  {
    id: 'expense' as TransactionType,
    title: 'Расход',
    subtitle: 'Трата средств',
    icon: '💸',
    color: 'bg-red-500',
    lightColor: 'bg-red-50',
    textColor: 'text-red-700'
  },
  {
    id: 'transfer' as TransactionType,
    title: 'Перевод',
    subtitle: 'Между счетами',
    icon: '🔄',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-700'
  }
];

export function TransactionTypeSelector({ onTypeSelect, selectedType }: TransactionTypeSelectorProps) {
  const [hoveredType, setHoveredType] = useState<TransactionType | null>(null);

  return (
    <div className="p-6 animate-slideUp">
      <div className="text-center mb-8">
        <div className="floating mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto shadow-lg">
            <span className="text-2xl">💼</span>
          </div>
        </div>
        <h2 className="text-2xl font-bold text-telegram-text mb-2">
          Новая транзакция
        </h2>
        <p className="text-telegram-hint">
          Выберите тип операции
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
        {transactionTypes.map((type) => {
          const isSelected = selectedType === type.id;
          const isHovered = hoveredType === type.id;

          return (
            <button
              key={type.id}
              onClick={() => onTypeSelect(type.id)}
              onMouseEnter={() => setHoveredType(type.id)}
              onMouseLeave={() => setHoveredType(null)}
              className={`
                w-full p-6 rounded-2xl border-2 transition-all duration-300 cubic-bezier(0.4, 0, 0.2, 1)
                ${isSelected
                  ? `${type.color} border-transparent text-white shadow-lg transform scale-105 animate-bounceIn`
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                }
                ${isHovered && !isSelected ? 'transform scale-102 shadow-lg' : ''}
                active:scale-95 apple-button
              `}
            >
              <div className="flex items-center space-x-4">
                <div className={`
                  text-3xl p-3 rounded-xl transition-colors duration-200
                  ${isSelected ? 'bg-white/20' : type.lightColor}
                `}>
                  {type.icon}
                </div>
                <div className="flex-1 text-left">
                  <h3 className={`
                    text-lg font-semibold transition-colors duration-200
                    ${isSelected ? 'text-white' : 'text-gray-900'}
                  `}>
                    {type.title}
                  </h3>
                  <p className={`
                    text-sm transition-colors duration-200
                    ${isSelected ? 'text-white/80' : 'text-gray-500'}
                  `}>
                    {type.subtitle}
                  </p>
                </div>
                <div className={`
                  w-6 h-6 rounded-full border-2 transition-all duration-200
                  ${isSelected
                    ? 'border-white bg-white'
                    : 'border-gray-300'
                  }
                `}>
                  {isSelected && (
                    <div className={`w-3 h-3 rounded-full ${type.color} m-0.5`} />
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}